<template>
  <dialog
    ref="dialogRef"
    class="inquiry-flow-dialog"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="dialogTitleId"
    @cancel.prevent="handleClose"
    @keydown.esc="handleClose"
    @click="handleDialogClick"
  >
    <div class="inquiry-flow-dialog__container">
      <!-- コンテンツ -->
      <div class="inquiry-flow-dialog__content">
        <!-- ステップ表示 -->
        <template v-if="isStep">
          <!-- Step 1: カテゴリ選択 -->
          <StepCategory
            v-if="currentStep === 'category'"
            v-model="formData.category"
            :keyword="keyword"
            @next="handleStepNext"
            @back="handleClose"
            @skip="handleSkip"
          />

          <!-- Step 2: 期間選択 -->
          <StepDuration
            v-if="currentStep === 'duration'"
            v-model="formData.duration"
            :keyword="keyword"
            @next="handleStepNext"
            @back="handleStepBack"
            @skip="handleSkip"
          />

          <!-- Step 3: 受診状況選択 -->
          <StepMedicalVisit
            v-if="currentStep === 'medicalVisit'"
            v-model="formData.medicalVisit"
            :keyword="keyword"
            @next="handleStepNext"
            @back="handleStepBack"
            @skip="handleSkip"
          />

          <!-- Step 4: 自由入力 -->
          <StepFreeText
            v-if="currentStep === 'freeText'"
            v-model="formData.freeText"
            :keyword="keyword"
            @search="handleSearch"
            @back="handleStepBack"
          />
        </template>

        <!-- ローディング -->
        <LoadingBar v-if="isLoading" />

        <!-- 検索結果（0件・エラー表示も内部で処理） -->
        <SearchResults
          v-if="isResult"
          v-model="fullFormData"
          :results="results"
          :has-error="hasError"
          :keyword="keyword"
          :is-loading="isLoading"
          @close="handleClose"
          @retry="handleRetry"
          @re-search="handleSearch"
        />
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue';
import StepCategory from './StepCategory.vue';
import StepDuration from './StepDuration.vue';
import StepMedicalVisit from './StepMedicalVisit.vue';
import StepFreeText from './StepFreeText.vue';
import LoadingBar from '../../similar_search/components/LoadingBar.vue';
import SearchResults from './SearchResults.vue';
import { searchSimilarTopics } from '../../similar_search/api';
import { buildSearchQuery } from '../buildSearchQuery';
import type {
  InquiryStep,
  InquiryFormData,
  TargetAge,
  TargetGender,
  SimilarSearchResult,
} from '../types';

const props = defineProps<{
  visible: boolean;
  keyword: string;
  targetAge: TargetAge;
  targetGender: TargetGender;
}>();

const emit = defineEmits<{
  close: [];
}>();

// DOM refs
const dialogRef = ref<HTMLDialogElement | null>(null);

// ダイアログタイトル ID（アクセシビリティ用）
const dialogTitleId = 'inquiry-flow-dialog-title';

// ステップ管理（4つのステップのみ）
const currentStep = ref<InquiryStep>('category');

// 非同期状態管理（独立変数）
const isLoading = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);

// フォームデータ（propsの年代・性別も含めて管理する）
const formData = reactive<InquiryFormData>({
  keyword: props.keyword,
  targetAge: props.targetAge,
  targetGender: props.targetGender,
  category: null,
  duration: null,
  medicalVisit: null,
  freeText: '',
});

// 検索結果
const results = ref<SimilarSearchResult[]>([]);

// ステップの順序定義
const stepOrder: InquiryStep[] = [
  'category',
  'duration',
  'medicalVisit',
  'freeText',
];

// 派生状態（computed）
const isStep = computed(() => !hasSearched.value && !isLoading.value);
const isResult = computed(() => hasSearched.value && !isLoading.value);

// fullFormData を computed (getter/setter) で管理
// SearchResults に v-model で渡すため
const fullFormData = computed({
  get: (): InquiryFormData => formData,
  set: (value: InquiryFormData) => {
    // キーワードは親コンポーネントから受け取った値を維持
    formData.keyword = props.keyword;
    // その他すべての値を更新
    formData.targetAge = value.targetAge;
    formData.targetGender = value.targetGender;
    formData.category = value.category;
    formData.duration = value.duration;
    formData.medicalVisit = value.medicalVisit;
    formData.freeText = value.freeText;
  },
});

// ダイアログの状態をリセット
const resetDialog = () => {
  // ステップをリセット
  currentStep.value = 'category';

  // 非同期状態をリセット
  isLoading.value = false;
  hasSearched.value = false;
  hasError.value = false;

  // フォームデータをリセット - propsの値を使って初期化
  formData.keyword = props.keyword;
  formData.targetAge = props.targetAge;
  formData.targetGender = props.targetGender;
  formData.category = null;
  formData.duration = null;
  formData.medicalVisit = null;
  formData.freeText = '';

  // 検索結果をリセット
  results.value = [];
};

// ダイアログの表示制御
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      resetDialog();
      await nextTick();
      dialogRef.value?.showModal();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true },
);

// 次のステップへ
const handleStepNext = () => {
  const currentIndex = stepOrder.indexOf(currentStep.value);
  const currentStepName = currentStep.value;
  let nextStepName: InquiryStep = 'category';

  if (currentIndex < stepOrder.length - 1) {
    nextStepName = stepOrder[currentIndex + 1];
    currentStep.value = nextStepName;

    // ステップ完了時にGA送信
    // 現在のステップの回答内容を取得
    let answerValue = '';
    if (currentStepName === 'category' && formData.category) {
      answerValue = formData.category;
    } else if (currentStepName === 'duration' && formData.duration) {
      answerValue = formData.duration;
    } else if (currentStepName === 'medicalVisit' && formData.medicalVisit) {
      answerValue = formData.medicalVisit;
    }

    window.sendGAEvent(
      '問診LP',
      'answer',
      `step_${currentStepName}_${answerValue}`
    );
  }
};

// 前のステップへ
const handleStepBack = () => {
  const currentIndex = stepOrder.indexOf(currentStep.value);
  if (currentIndex > 0) {
    currentStep.value = stepOrder[currentIndex - 1];
  } else {
    handleClose();
  }
};

// スキップ（次のステップへ進む）
const handleSkip = () => {
// スキップ時にGA送信
  window.sendGAEvent(
    '問診LP',
    'skip',
    `step_${currentStep.value}`
  );
  handleStepNext();
};

// 検索実行
const handleSearch = async () => {
  // 検索実行時にGA送信
  window.sendGAEvent(
    '問診LP',
    'search',
    currentStep.value === 'freeText' && formData.freeText ?
      `freeText_length_${formData.freeText.length}` : 'from_steps'
  );

  isLoading.value = true;
  hasSearched.value = false;
  hasError.value = false;

  try {
    // computed の fullFormData からクエリを構築
    const query = buildSearchQuery(fullFormData.value);

    const response = await searchSimilarTopics({ query });
    results.value = response.results;
    hasSearched.value = true;

    // 検索結果取得時にGA送信
    window.sendGAEvent(
      '問診LP',
      'result',
      `${response.results.length}件`
    );
  } catch (error) {
    console.error('検索エラー:', error);
    hasError.value = true;
    hasSearched.value = true;
  } finally {
    isLoading.value = false;
  }
};

// リトライ（エラー時・0件時）
const handleRetry = () => {
  // 検索状態をリセット
  hasSearched.value = false;
  hasError.value = false;
  results.value = [];

  // ステップを最初に戻す
  currentStep.value = 'category';
};

// ダイアログを閉じる
const handleClose = () => {
  emit('close');
};

// ダイアログのクリックイベント（backdrop クリック検出）
const handleDialogClick = (e: MouseEvent) => {
  const dialogElement = dialogRef.value;
  if (!dialogElement) return;

  // クリックが dialog 要素自体（backdrop 部分）の場合のみ閉じる
  if (e.target === dialogElement) {
    handleClose();
  }
};
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.inquiry-flow-dialog {
  padding: 0;
  border: none;
  border-radius: 12px;
  max-width: 600px;
  width: calc(100% - 32px);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  // dialog 要素の showModal() は自動的に中央配置される
  // margin: auto で垂直・水平中央を保証
  margin: auto;

  &::backdrop {
    background: rgba(0, 0, 0, 0.6);
  }

  &__container {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    background: #fcfaf6;
    text-align: left;
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
  }

  &__error-icon {
    font-size: 48px;
    color: #d32f2f;
    margin-bottom: 16px;

    .material-icons {
      font-size: inherit;
    }
  }

  &__error-message {
    font-size: 1rem;
    color: $base-font-color;
    margin: 0 0 24px;
    line-height: 1.6;
  }
}

// レスポンシブ対応
@media (max-width: 640px) {
  .inquiry-flow-dialog {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;

    &__container {
      max-height: 100%;
      height: 100%;
    }
  }
}
</style>
